import React from "react";
import { AlertTriangle } from "lucide-react";

/**
 * Scoped error boundary for the Super Admin route tree.
 *
 * Without this, a single ReferenceError in one page (e.g. missing icon
 * import) unmounts the whole layout — top nav, sidebar, banner all
 * disappear. With it, only the current <Outlet /> cell shows a
 * readable error card, the rest of the shell survives, and the next
 * route change re-mounts cleanly.
 */
export default class SuperAdminErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, info: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        this.setState({ info });
        // Keep this console.error — ops relies on the browser console when
        // the production Sentry integration isn't in yet.
        // eslint-disable-next-line no-console
        console.error("SuperAdmin boundary caught:", error, info);
    }

    reset = () => this.setState({ error: null, info: null });

    render() {
        const { error } = this.state;
        if (!error) return this.props.children;
        return (
            <div
                className="rounded-xl border border-red-400/30 bg-red-400/5 p-6 text-[13px] text-red-200"
                data-testid="sa-error-boundary"
            >
                <div className="mb-2 flex items-center gap-2 text-[14px] font-semibold">
                    <AlertTriangle size={14} /> Something went wrong on this page.
                </div>
                <pre className="mt-2 max-h-[40vh] overflow-auto whitespace-pre-wrap break-words rounded-md bg-black/40 p-3 text-[11px] text-red-100/85">
                    {String(error?.stack || error?.message || error)}
                </pre>
                <div className="mt-3 flex items-center gap-2">
                    <button
                        onClick={this.reset}
                        className="rounded-md border border-white/10 px-3 py-1 text-[12px] text-white/80 hover:bg-white/5"
                    >
                        Try again
                    </button>
                    <a
                        href="/superadmin"
                        className="text-[12px] text-[#00C2FF] hover:underline"
                    >
                        Back to Overview
                    </a>
                </div>
            </div>
        );
    }
}
